"use client";

import blocks from "@/components/page/blocks.module.css";
import {
  decimal,
  optimalDepth,
  perplexitySource,
  perplexitySweep,
  type PerplexityPoint,
} from "@/content/research-data";
import styles from "./charts.module.css";
import { useChartFrame } from "./useChartFrame";

export type PerplexityChartProps = {
  /** Barrido de vueltas (por defecto, el real de research-data). */
  data?: PerplexityPoint[];
  /** Vuelta que se marca como óptima. */
  optimal?: number;
  /** Muestra la curva con parada adaptativa (punteada). */
  showHalting?: boolean;
  /** Versión corta: menos alto, menos texto en el pie. */
  compact?: boolean;
  /**
   * Vuelta elegida desde afuera (control de 1 a 16 en Investigación): dibuja una guía
   * vertical y un punto sobre la curva. El valor a mostrar lo arma la página con `data`.
   */
  selected?: number | null;
  title?: string;
  /** Oculta el título visible (si la sección ya lo dice) sin quitárselo al lector de pantalla. */
  hideTitle?: boolean;
  /** Sin la tabla desplegable (si la página ya muestra los datos en otra tabla). */
  hideTable?: boolean;
  id?: string;
  className?: string;
};

// Dominio fijo del eje: el barrido vive entre 49 y 70. Empezar en 46 deja aire debajo
// de la meseta sin exagerar la caída (el eje está rotulado y la tabla da los valores).
const Y_MIN = 46;
const Y_MAX = 72;
const Y_TICKS = [50, 60, 70];

/** Vueltas de deliberación vs perplejidad (guía 11, gráfico 1). */
export function PerplexityChart({
  data = perplexitySweep,
  optimal = optimalDepth,
  showHalting = true,
  compact = false,
  selected = null,
  title = "Pensar más vueltas mejora la predicción hasta la séptima.",
  hideTitle = false,
  hideTable = false,
  id = "grafico-vueltas",
  className,
}: PerplexityChartProps) {
  const { ref, width: w, inView } = useChartFrame<HTMLDivElement>(640);

  const narrow = w < 520;
  const h = Math.round(clamp(w * (compact ? 0.62 : 0.56), compact ? 240 : 280, compact ? 340 : 440));
  const m = { top: 34, right: narrow ? 14 : 24, bottom: 46, left: 38 };
  const pw = Math.max(1, w - m.left - m.right);
  const ph = Math.max(1, h - m.top - m.bottom);
  const first = data[0]?.depth ?? 1;
  const last = data[data.length - 1]?.depth ?? 16;
  const x = (d: number) => m.left + ((d - first) / Math.max(1, last - first)) * pw;
  const y = (v: number) => m.top + ((Y_MAX - v) / (Y_MAX - Y_MIN)) * ph;

  const forcedPath = linePath(data.map((p) => [x(p.depth), y(p.forced)]));
  const haltingPath = linePath(data.map((p) => [x(p.depth), y(p.halting)]));
  const opt = data.find((p) => p.depth === optimal);
  const start = data[0];
  const end = data[data.length - 1];
  const plateau = data.filter((p) => p.depth > optimal);
  // La llave de la meseta va por encima de las dos curvas, con aire fijo en píxeles.
  const bracketY =
    Math.min(...plateau.map((p) => Math.min(y(p.forced), showHalting ? y(p.halting) : Infinity)), h) - 12;
  const xTicks = narrow
    ? data.map((p) => p.depth).filter((d) => d === first || d === last || (d - first) % 3 === 0)
    : data.map((p) => p.depth);
  const sel = selected != null ? data.find((p) => p.depth === selected) : undefined;

  const summary = [
    start && `Perplejidad de ${decimal(start.forced, 2)} con ${start.depth} vuelta`,
    opt && `${decimal(opt.forced, 2)} con ${opt.depth} vueltas`,
    plateau.length > 0 && `sin cambios de ${plateau[0].depth} a ${plateau[plateau.length - 1].depth}`,
  ]
    .filter(Boolean)
    .join(", ");
  const haltingSummary =
    showHalting && end
      ? ` Con parada adaptativa, ${decimal(end.halting, 2)} usando ${decimal(end.haltingAvgIters, 1)} vueltas en promedio.`
      : "";

  const titleId = `${id}-titulo`;

  return (
    <figure
      id={id}
      className={[styles.chart, className].filter(Boolean).join(" ")}
      data-compact={compact ? "true" : undefined}
      data-inview={inView ? "true" : undefined}
      aria-labelledby={titleId}
    >
      <div className={styles.header}>
        <p id={titleId} className={hideTitle ? "sr-only" : styles.title}>
          {title}
        </p>
        <ul className={styles.legend} aria-hidden="true">
          <li>
            <span className={styles.swatch} />
            Vueltas fijas
          </li>
          {showHalting && end && (
            <li>
              <span className={styles.swatch} data-kind="dashed" />
              Parada adaptativa · usa {decimal(end.haltingAvgIters, 1)} en promedio
            </li>
          )}
        </ul>
      </div>

      <div ref={ref} className={styles.plot}>
        <svg
          className={styles.svg}
          viewBox={`0 0 ${w} ${h}`}
          width={w}
          height={h}
          role="img"
          aria-label={`${summary}.${haltingSummary}`}
        >
          {/* Eje Y: perplejidad */}
          <text className={styles.axisTitle} x={0} y={14}>
            perplejidad
          </text>
          {Y_TICKS.map((t) => (
            <g key={t}>
              <line className={styles.grid} x1={m.left} x2={w - m.right} y1={y(t)} y2={y(t)} />
              <text className={styles.tick} x={m.left - 8} y={y(t) + 4} textAnchor="end">
                {t}
              </text>
            </g>
          ))}
          <line className={styles.axis} x1={m.left} x2={w - m.right} y1={y(Y_MIN)} y2={y(Y_MIN)} />

          {/* Eje X: vueltas */}
          {xTicks.map((d) => (
            <text
              key={d}
              className={styles.tick}
              x={x(d)}
              y={y(Y_MIN) + 18}
              textAnchor="middle"
              data-strong={d === optimal ? "true" : undefined}
            >
              {d}
            </text>
          ))}
          <text className={styles.axisTitle} x={m.left + pw / 2} y={h - 6} textAnchor="middle">
            vueltas de deliberación
          </text>

          {/* Meseta: de la vuelta siguiente al óptimo hasta el final, el valor no cambia. */}
          {opt && plateau.length > 1 && (
            <g className={styles.fade}>
              <path
                className={styles.bracket}
                d={`M${x(plateau[0].depth)} ${bracketY + 5}V${bracketY}H${x(plateau[plateau.length - 1].depth)}V${bracketY + 5}`}
              />
              <text
                className={styles.note}
                x={(x(plateau[0].depth) + x(plateau[plateau.length - 1].depth)) / 2}
                y={bracketY - 7}
                textAnchor="middle"
              >
                {narrow ? "meseta" : `meseta: ${decimal(plateau[plateau.length - 1].forced, 2)} de ${plateau[0].depth} a ${plateau[plateau.length - 1].depth}`}
              </text>
            </g>
          )}

          {showHalting && <path className={`${styles.lineAlt} ${styles.fade}`} d={haltingPath} />}
          <path className={`${styles.lineOurs} ${styles.draw}`} d={forcedPath} pathLength={1} />

          {/* Primer valor, para leer la caída sin ir a la tabla. */}
          {start && (
            <g className={styles.fade}>
              <circle className={styles.dot} cx={x(start.depth)} cy={y(start.forced)} r={2.5} />
              <text className={styles.note} x={x(start.depth) + 8} y={y(start.forced) + 4}>
                {decimal(start.forced, 2)}
              </text>
            </g>
          )}

          {/* El óptimo: punto de acento con una línea guía hacia arriba, donde hay aire. */}
          {opt && (
            <g className={styles.fade}>
              <line className={styles.leader} x1={x(opt.depth)} x2={x(opt.depth)} y1={y(opt.forced) - 9} y2={m.top + 24} />
              <text className={styles.noteAccent} x={x(opt.depth) + 6} y={m.top + 4}>
                óptimo: {opt.depth} vueltas
              </text>
              <text className={styles.note} x={x(opt.depth) + 6} y={m.top + 18}>
                perplejidad {decimal(opt.forced, 2)}
              </text>
              <circle className={styles.halo} cx={x(opt.depth)} cy={y(opt.forced)} r={8} />
              <circle className={styles.dot} cx={x(opt.depth)} cy={y(opt.forced)} r={4} />
            </g>
          )}

          {sel && (
            <g aria-hidden="true">
              <line
                className={styles.cursor}
                x1={0}
                x2={0}
                y1={m.top}
                y2={y(Y_MIN)}
                style={{ transform: `translateX(${x(sel.depth)}px)` }}
              />
              <circle
                className={styles.cursorDot}
                cx={0}
                cy={0}
                r={5}
                style={{ transform: `translate(${x(sel.depth)}px, ${y(sel.forced)}px)` }}
              />
            </g>
          )}
        </svg>
      </div>

      <figcaption className={styles.fine}>
        <span>Perplejidad: qué tan sorprendido queda el modelo ante el texto real. Menos es mejor.</span>
        {!compact && (
          <span>
            Con la parada adaptativa, el modelo decide solo cuántas vueltas usar. Su curva sigue a la de vueltas fijas
            muy de cerca.
          </span>
        )}
        <span>
          Fuente: barrido de 1 a 16 vueltas sobre el mismo checkpoint, {perplexitySource.dataset},{" "}
          {perplexitySource.rows} ejemplos ({perplexitySource.file}). Una sola corrida, sin barras de error.
        </span>
      </figcaption>

      {!hideTable && (
        <details className={styles.data}>
          <summary>Ver los datos</summary>
          <div className={`${styles.dataBody} ${blocks.tableWrap}`}>
            <table className={blocks.table}>
              <caption>Perplejidad por número de vueltas de deliberación (menos es mejor)</caption>
              <thead>
                <tr>
                  <th scope="col">Vueltas</th>
                  <th scope="col" className={blocks.num}>
                    Vueltas fijas
                  </th>
                  {showHalting && (
                    <>
                      <th scope="col" className={blocks.num}>
                        Parada adaptativa
                      </th>
                      <th scope="col" className={blocks.num}>
                        Vueltas usadas (promedio)
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.map((p) => (
                  <tr key={p.depth}>
                    <th scope="row">
                      {p.depth}
                      {p.depth === optimal ? " · óptimo" : ""}
                    </th>
                    <td className={blocks.num}>{decimal(p.forced, 2)}</td>
                    {showHalting && (
                      <>
                        <td className={blocks.num}>{decimal(p.halting, 2)}</td>
                        <td className={blocks.num}>{decimal(p.haltingAvgIters, 2)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </figure>
  );
}

function clamp(v: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, v));
}

function linePath(points: Array<[number, number]>) {
  return points.map(([px, py], i) => `${i === 0 ? "M" : "L"}${px.toFixed(1)} ${py.toFixed(1)}`).join("");
}
